import { orderTypeConstants } from "constants/order";
import { distributionLogicValueList } from "constants/paymentPlanConstants";
import { mapPayerNameToCodeForDropdown } from "constants/practiceType";
import dayjs from "dayjs";
import { parseDate, parseTime } from "helpers/date";
import { removeClearPatientFee } from "helpers/fee";
import { centsToDollars } from "helpers/price";
import { usePayerInformation } from "hooks/usePayerInformation";
import { CLEAR_HEALTH_PAYER_NAME } from "hooks/useProviderProcedure";
import { useMemo, useEffect, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useSearchProviderProcedureMutation } from "store/queries/providerProcedure";
import {
  setAutoCompleteValue,
  setRows,
  setSelectedProcedures,
} from "store/slices/serviceDetails";
import {
  IOrder,
  IGFEServiceDetails,
  IDefaultServiceDetails,
  IPatientResponsibilityServiceDetails,
} from "types/Order/Order";

import {
  VisitForm,
  Submit,
  PatientInfoForm,
  OrderType,
  ServiceDetails,
  Communication,
  PatientBenefitsSection,
} from "./components";
import { PaymentPlan } from "./components/PaymentPlan/PaymentPlan";
import { RedoHeader } from "./components/RedoHeader";
import {
  buildSelectedProceduresArray,
  buildSelectedProceduresObject,
  buildSelectedProceduresToTable,
} from "./components/ServiceDetails/builders";
import { formatPhoneNumberToMask } from "./utils/mask";

export function RedoOrder({
  order,
  handleSwitchRedoOrder,
}: {
  order: IOrder;
  handleSwitchRedoOrder: () => void;
}) {
  const dispatch = useDispatch();
  const [searchMutation] = useSearchProviderProcedureMutation();
  const [currentProviderProcedures, setCurrentProviderProcedures] =
    useState<any>([]);

  const {
    serviceDetails,
    patient,
    procedures,
    providerProcedures,
    amountInCents,
    accountNumber,
    dateOfService,
    orderType,
    accountId,
    gfeQuote,
    gfeQuoteId,
    id,
    paymentPlan,
    account,
    outreachStopped,
  } = order;

  const { payerNameOptions } = usePayerInformation(accountId);

  const estimatedVisitCost = useMemo(() => {
    if (orderType === orderTypeConstants.GFE) {
      return centsToDollars(
        (serviceDetails as IGFEServiceDetails)
          .patientResponsibilityEstimateAmountInCents || 0
      );
    }
    return centsToDollars(
      (serviceDetails as IDefaultServiceDetails).estimatedVisitCost || 0
    );
  }, [orderType, serviceDetails]);

  const payerName = useMemo(() => {
    if (orderType === orderTypeConstants.GFE) {
      return payerNameOptions.find(
        (aOption: { name: string; value: string }) =>
          aOption.name.toLowerCase() === order?.payer?.name?.toLowerCase()
      );
    }
    return null;
  }, [orderType, payerNameOptions, order?.payer?.name]);

  const payerName2 = useMemo(() => {
    if (orderType === orderTypeConstants.GFE) {
      return payerNameOptions.find(
        (aOption: { name: string; value: string }) =>
          aOption?.name?.toLowerCase() ===
          order?.secondaryPayer?.name?.toLowerCase()
      );
    }
    return null;
  }, [orderType, payerNameOptions, order?.secondaryPayer?.name]);

  const paymentPlanActive = useMemo(
    () => paymentPlan.find((paymentPlan) => paymentPlan.active),
    [paymentPlan]
  );

  const selectedProceduresList = useMemo(() => {
    return serviceDetails.procedures.map(
      (procedure) => `${procedure.code} - ${procedure.cptDescription}`
    );
  }, [serviceDetails]);

  const sendDefaultText = useMemo(() => {
    if (
      order.scheduledFirstCommunicationDate &&
      !order.scheduledFirstCommunicationSent &&
      order.scheduledCommunicationCustomText
    ) {
      return false;
    }
    return true;
  }, [order]);

  // Calculate downpayment only flat amount with clear patient fee adjustment
  const getDownpaymentOnlyFlatValue = useMemo(() => {
    if (
      paymentPlanActive?.paymentPlanMinDownPaymentFlat === null ||
      paymentPlanActive?.paymentPlanMinDownPaymentFlat === undefined
    ) {
      return null;
    }
    const valueInDollars = centsToDollars(
      paymentPlanActive.paymentPlanMinDownPaymentFlat
    );
    // Downgrade value if order has clear patient fee (for GFE orders)
    if (
      orderType === orderTypeConstants.GFE &&
      order.clearPatientFeeValueInPercentage
    ) {
      return removeClearPatientFee(
        valueInDollars,
        order.clearPatientFeeValueInPercentage
      );
    }
    return valueInDollars;
  }, [
    paymentPlanActive?.paymentPlanMinDownPaymentFlat,
    orderType,
    order.clearPatientFeeValueInPercentage,
  ]);

  const initializeDefault = async () => {
    const autoCompleteValue = [
      ...new Set(
        serviceDetails.procedures.map(
          (procedure) =>
            `${procedure.code} - ${
              procedures?.find((p) => p.cptCode === procedure.code.toString())
                ?.description
            }`
        )
      ),
    ];
    const selectedProcedureArrayWithDefaultDescriptions =
      buildSelectedProceduresArray({
        procedures: (serviceDetails as IGFEServiceDetails).procedures.map(
          (procedure) => {
            const defaultDescription = procedures?.find(
              (p) => p.cptCode === procedure.code.toString()
            )?.description;
            return {
              code: procedure.code,
              providerProcedureId: procedure.providerProcedureId,
              specialty: procedure.specialty,
              cptDescription: defaultDescription || "",
            };
          }
        ),
      });
    const selectedProcedureArray = buildSelectedProceduresArray({
      procedures: (serviceDetails as IGFEServiceDetails).procedures.map(
        (procedure) => {
          return {
            code: procedure.code,
            providerProcedureId: procedure.providerProcedureId,
            specialty: procedure.specialty,
            cptDescription: procedure.cptDescription,
          };
        }
      ),
    });
    const selectedProceduresObject = buildSelectedProceduresObject(
      selectedProcedureArray
    );
    const rowsToSet = buildSelectedProceduresToTable({
      selectedProceduresObject,
      procedures: procedures || [],
      providerProcedures: providerProcedures || [],
      rows: [],
      // Don't pass originalProcedures here - we'll match after fetching all providers
    });
    const rowsWithoutDuplicateProviders = rowsToSet.map((row: any) => {
      const uniqueProviders = row.providerDropdown.filter(
        (provider: any, index: any, self: any) =>
          index === self.findIndex((p: any) => p.id === provider.id)
      );
      return {
        ...row,
        providerDropdown: uniqueProviders,
      };
    });

    // We'll set the correct amount after matching providers in rowsWithAllAvailableProviders
    const rowsWithCurrentAmount = rowsWithoutDuplicateProviders;

    const fetchedProviderProcedures: any[] = await Promise.all(
      selectedProceduresObject.map(async (procedure) => {
        const result = (await searchMutation({
          accountId,
          cptCode: procedure.code,
          payerName:
            orderType === orderTypeConstants.GFE
              ? serviceDetails.payerName
              : CLEAR_HEALTH_PAYER_NAME,
        })) as any;
        return result.data.data.data;
      })
    ).then((results) => results.flat());

    const rowsWithAllAvailableProviders = rowsWithCurrentAmount.map((row) => {
      const providerData = [...row.providerDropdown];
      fetchedProviderProcedures.forEach((providerProcedure) => {
        if (
          row.cptCode === providerProcedure.procedure.cptCode &&
          row.specialty === providerProcedure.provider.specialty &&
          row.procedureId === providerProcedure.procedureId &&
          !providerData.find((p) => p.id === providerProcedure.provider.id)
        ) {
          providerData.push({
            id: providerProcedure.provider.id,
            name: providerProcedure.provider.name,
            specialty: providerProcedure.provider.specialty,
            priceInCents: providerProcedure.priceInCents,
          });
        }
      });

      // Now match the provider from the original order
      const originalProcedure = (
        serviceDetails as IGFEServiceDetails
      ).procedures.find(
        (p) => p.code === row.cptCode && p.specialty === row.specialty
      );

      let { selectedProvider } = row;
      let selectedAmount = row.amount;
      let selectedProviderProcedureId = row.providerProcedureId;

      if (originalProcedure?.providerId) {
        const matchingProvider = providerData.find(
          (p) => p.id === originalProcedure.providerId?.toString()
        );

        if (matchingProvider) {
          selectedProvider = matchingProvider;
          // Use the custom amount from the original order if it exists, otherwise use provider's default price
          selectedAmount =
            originalProcedure.amountInCents !== undefined &&
            originalProcedure.amountInCents !== null
              ? originalProcedure.amountInCents
              : matchingProvider.priceInCents;

          // Find the matching providerProcedure to get the correct providerProcedureId
          const matchingProviderProcedure = fetchedProviderProcedures.find(
            (pp: any) =>
              pp.providerId === originalProcedure.providerId?.toString() &&
              pp.provider.specialty === row.specialty &&
              pp.procedure.cptCode === row.cptCode
          );
          if (matchingProviderProcedure) {
            selectedProviderProcedureId = matchingProviderProcedure.id;
          }
        }
      }

      return {
        ...row,
        providerDropdown: providerData,
        selectedProvider,
        amount: selectedAmount,
        providerProcedureId: selectedProviderProcedureId,
      };
    });
    const rowsWithBenefitSelectionType = rowsWithAllAvailableProviders.map(
      (row) => {
        const procedure = serviceDetails.procedures.find(
          (p) =>
            p.code === row.cptCode &&
            p.specialty === row.specialty &&
            p.providerId === row.selectedProvider.id &&
            p.providerProcedureId === row.providerProcedureId
        );
        return {
          ...row,
          benefitSelectionType:
            procedure?.benefitsForProvProc?.benefitSelectionType,
        };
      }
    );
    dispatch(setAutoCompleteValue(autoCompleteValue));
    dispatch(
      setSelectedProcedures(selectedProcedureArrayWithDefaultDescriptions)
    );
    dispatch(setRows(rowsWithBenefitSelectionType));
    setCurrentProviderProcedures(fetchedProviderProcedures);
  };

  useEffect(() => {
    if (orderType !== orderTypeConstants.PATIENT_RESPONSIBILITY) {
      initializeDefault();
    }
  }, []);

  const methods = useForm({
    defaultValues: {
      email: patient.email,
      amount: centsToDollars(
        (serviceDetails as IGFEServiceDetails)
          .patientResponsibilityEstimateAmountInCents || 0
      ),
      lastName: patient.lastName,
      firstName: patient.firstName,
      dateOfBirth: parseDate(patient.dateOfBirth),
      totalAmount: centsToDollars(amountInCents || 0),
      phoneNumber: patient.phoneNumber
        ? formatPhoneNumberToMask(patient.phoneNumber)
        : null,
      accountNumber,
      accountLocationId:
        order?.accountLocation?.id ?? order?.accountLocationId ?? null,
      dateOfService: parseDate(dateOfService),
      timeOfService: parseTime(dateOfService),
      serviceDetails: serviceDetails.procedures,
      preferredContactMethod: patient.preferredContactMethod,
      estimatedVisitCostForPatient: estimatedVisitCost,
      orderType,
      accountId,
      payerName: payerName?.name || null,
      payerName2: payerName2?.name || null,
      payer: null,
      payer2: null,
      deductible: centsToDollars(gfeQuote?.remainingDeductibleInCents || 0),
      outOfPocketMax: centsToDollars(gfeQuote?.outOfPocketMaxInCents || 0),
      flatCopay: centsToDollars(gfeQuote?.flatCopayInCents || 0),
      professionalFlatCopay:
        order?.benefitDetails?.response?.professionalCopay ||
        centsToDollars(gfeQuote?.professionalFlatCopayInCents || 0),
      showProfessionalCopay:
        (order?.benefitDetails?.response?.professionalCopay || 0) > 0 ||
        (gfeQuote?.professionalFlatCopayInCents || 0) > 0,
      coinsurancePercent: gfeQuote?.coinsurancePercent || 0,
      deductible2: centsToDollars(
        gfeQuote?.secondaryPatientBenefits?.deductible || 0
      ),
      outOfPocketMax2: centsToDollars(
        gfeQuote?.secondaryPatientBenefits?.outOfPocketMax || 0
      ),
      flatCopay2: centsToDollars(
        gfeQuote?.secondaryPatientBenefits?.flatCopay || 0
      ),
      professionalFlatCopay2:
        order?.secondaryBenefitDetails?.response?.professionalCopay ||
        centsToDollars(
          gfeQuote?.secondaryPatientBenefits?.professionalFlatCopay || 0
        ),
      showProfessionalCopay2:
        (order?.secondaryBenefitDetails?.response?.professionalCopay || 0) >
          0 ||
        (gfeQuote?.secondaryPatientBenefits?.professionalFlatCopay || 0) > 0,
      coinsurancePercent2:
        gfeQuote?.secondaryPatientBenefits?.coinsurancePercent || 0,
      gfeQuoteId,
      practiceType:
        order?.benefitDetails?.request?.practiceTypeCode ||
        mapPayerNameToCodeForDropdown[0]?.value,
      practiceType2:
        order?.secondaryBenefitDetails?.request?.practiceTypeCode ||
        mapPayerNameToCodeForDropdown[0]?.value,
      outOfPocketReason: serviceDetails?.outOfPocketReason
        ? { id: "", name: serviceDetails?.outOfPocketReason }
        : null,
      orderId: id,
      subscriberFirstName: patient.firstName,
      subscriberLastName: patient.lastName,
      subscriberDOB: parseDate(patient.dateOfBirth),
      memberId: patient?.patientBenefits?.memberID || null,
      memberId2: patient?.secondaryPatientBenefits?.memberID || null,
      isSubscriberPatient:
        patient?.patientBenefits?.isSubscriberPatient || null,
      paymentPlanDuration: paymentPlanActive?.paymentPlanDuration || null,
      paymentPlanDurationFull:
        (paymentPlanActive?.paymentPlanDuration || 0) + 1 || null,
      paymentPlanMinDownPaymentType:
        paymentPlanActive?.paymentPlanMinDownPaymentFlat !== null &&
        paymentPlanActive?.paymentPlanMinDownPaymentFlat !== undefined
          ? "Flat amount"
          : "Percentage",
      paymentPlanMinDownPaymentFlat:
        paymentPlanActive?.paymentPlanMinDownPaymentFlat !== null &&
        paymentPlanActive?.paymentPlanMinDownPaymentFlat !== undefined
          ? centsToDollars(paymentPlanActive.paymentPlanMinDownPaymentFlat)
          : null,
      paymentPlanMinDownPaymentPercent:
        paymentPlanActive?.paymentPlanMinDownPaymentPercent !== null
          ? paymentPlanActive?.paymentPlanMinDownPaymentPercent
          : null,
      paymentPlanEnabled: !!paymentPlanActive,
      redoOrderFirstRender: true,
      outreachEnabled: !outreachStopped,
      providerDistributionLogic:
        paymentPlanActive?.providerDistributionLogic ||
        account?.providerDistributionLogic ||
        distributionLogicValueList.BY_PRIORITY,
      facilityCopay: !!gfeQuote?.costApplicationRules?.facilityRules?.useCopay,
      facilityDeductible:
        !!gfeQuote?.costApplicationRules?.facilityRules?.useDeductible,
      facilityCoinsurance:
        !!gfeQuote?.costApplicationRules?.facilityRules?.useCoinsurance,
      professionalCopay:
        !!gfeQuote?.costApplicationRules?.nonFacilityRules?.useCopay,
      professionalDeductible:
        !!gfeQuote?.costApplicationRules?.nonFacilityRules?.useDeductible,
      professionalCoinsurance:
        !!gfeQuote?.costApplicationRules?.nonFacilityRules?.useCoinsurance,
      secondaryInsuranceActivated: !!order?.secondaryPayerId,
      facilityCopay2:
        !!gfeQuote?.secondaryPatientBenefits?.costApplicationRules
          ?.facilityRules?.useCopay,
      facilityDeductible2:
        !!gfeQuote?.secondaryPatientBenefits?.costApplicationRules
          ?.facilityRules?.useDeductible,
      facilityCoinsurance2:
        !!gfeQuote?.secondaryPatientBenefits?.costApplicationRules
          ?.facilityRules?.useCoinsurance,
      professionalCopay2:
        !!gfeQuote?.secondaryPatientBenefits?.costApplicationRules
          ?.nonFacilityRules?.useCopay,
      professionalDeductible2:
        !!gfeQuote?.secondaryPatientBenefits?.costApplicationRules
          ?.nonFacilityRules?.useDeductible,
      professionalCoinsurance2:
        !!gfeQuote?.secondaryPatientBenefits?.costApplicationRules
          ?.nonFacilityRules?.useCoinsurance,
      benefitsUpdatedAt: gfeQuote?.benefitsUpdatedAt || null,
      secondaryBenefitsUpdatedAt:
        gfeQuote?.secondaryPatientBenefits?.benefitsUpdatedAt || null,
      benefitDetails: order?.benefitDetails || null,
      benefitDetails2: order?.secondaryBenefitDetails || null,
      ...(orderType === orderTypeConstants.PATIENT_RESPONSIBILITY && {
        totalInsuranceAllowable:
          (serviceDetails as IPatientResponsibilityServiceDetails)
            ?.totalInsuranceAllowableInCents === null
            ? null
            : centsToDollars(
                (serviceDetails as IPatientResponsibilityServiceDetails)
                  ?.totalInsuranceAllowableInCents || 0
              ),
      }),
      scheduledFirstCommunicationDate: order?.scheduledFirstCommunicationDate
        ? dayjs.utc(order.scheduledFirstCommunicationDate).toDate()
        : null,
      scheduledFirstCommunicationSent:
        order?.scheduledFirstCommunicationSent || false,
      scheduledCommunicationCustomText:
        order?.scheduledCommunicationCustomText || null,
      sendDefaultText,
      initialClearPatientFeeValueInPercentage:
        order?.clearPatientFeeValueInPercentage
          ? Number(order?.clearPatientFeeValueInPercentage)
          : null,
      initialClearFeeValueInPercentage: order?.clearFeeValueInPercentage
        ? Number(order?.clearFeeValueInPercentage)
        : null,
      isDownPaymentOnly: paymentPlanActive?.isDownPaymentOnly || false,
      downpaymentOnlyType:
        paymentPlanActive?.paymentPlanMinDownPaymentFlat !== null &&
        paymentPlanActive?.paymentPlanMinDownPaymentFlat !== undefined
          ? "Flat amount"
          : "Percentage",
      downpaymentOnlyPercent:
        paymentPlanActive?.paymentPlanMinDownPaymentPercent !== null
          ? paymentPlanActive?.paymentPlanMinDownPaymentPercent
          : null,
      downpaymentOnlyFlatAmount: getDownpaymentOnlyFlatValue,
    },
  });

  return (
    <FormProvider {...methods}>
      {account && (
        <RedoHeader
          accountName={account.name}
          handleSwitchRedoOrder={handleSwitchRedoOrder}
        />
      )}
      <OrderType
        orderTypesVisible={account?.orderTypesVisible}
        isFetching={false}
        redo
      />
      <PatientInfoForm />
      <VisitForm account={account} skipDuplicateCheck isRedo order={order} />
      <PatientBenefitsSection
        costApplicationRules={gfeQuote?.costApplicationRules}
        account={account}
        redo
      />
      <ServiceDetails
        redo
        selectedProceduresList={selectedProceduresList}
        totalAmount={order.amountInCents}
        currentProviderProcedures={currentProviderProcedures}
      />
      {!!account && (
        <PaymentPlan
          accountData={{ data: account }}
          paymentPlan={paymentPlanActive}
          isRedo
        />
      )}
      {account && <Communication account={account} order={order} redo />}
      <Submit redo />
    </FormProvider>
  );
}
